<template>
    <v-dialog class="v-dialog--fullscreen" value="1"
    >
        <v-container class="v-dialog--fullscreen">
            <v-container class="d-flex align-stretch" style="height:100%">
                <v-card class="white" flat style="width:70%">
                    <svg id='drawArea' ref='drawAreaRef' style='height:100%;  width:100%;'></svg>
                </v-card>
                <v-card class="white" flat style="width:30%">
                    <v-card-title>{{header}}</v-card-title>
                    <v-card-text>
                        <div v-html="text"/>
                    </v-card-text>
                    <v-card-action>
                        <v-btn class="ma-4" v-if="!showButton" @click="nextState">Next</v-btn><v-btn class='ma-4' v-if="showButton" to="{ name: 'home'}" >Explore the MetroSets Tool</v-btn>
                    </v-card-action>
                </v-card>
            </v-container>
        </v-container>
    </v-dialog>
</template>

<script>
import * as d3 from 'd3'
import * as d3Scale from 'd3-scale'
import * as d3Shape from 'd3-shape'
import jsonStep3 from '../assets/poster_step3.json'
import jsonStep4 from '../assets/poster_step4.json'

export default {
  name: 'Poster',
  data () {
    return {
      header: 'MetroSets',
      state: 0,
      data: null,
      svg: null,
      dW: 0,
      dH: 0,
      text: 'Our paper, MetroSets: Visualizing Sets as Metro Maps, was accepted at IEEE VIS 2020 (<a target="blank" href="https://arxiv.org/abs/2008.09367">arXiv</a>). It makes considerable use of graph theoretic tools, and so we believe it is of interest for the graph drawing community as well.<br><br> Buckle in and take the digital poster tour of the MetroSets system!<br><br>Authors: Ben Jacobsen, Markus Wallinger, Stephen Kobourov, Martin Nöllenburg',
      lineSet: {},
      line: null,
      lineStroke: 6,
      nodeLineSet: {},
      nodeDummy: {},
      lineNodeList1: {},
      lineNodeList2: {},
      colors: {},
      showButton: false,
      numNodes: 0
    }
  },

  methods: {
    nextState () {
      switch (this.state) {
        case 0:
          this.header = 'MetroSets'
          this.text = '"MetroSets is a web tool for visualizing set systems using the metro map metaphor. In such visualizations, sets are drawn as metro lines, while elements are drawn as stations. If an element belongs to multiple sets it is depicted as an interchange station. This metaphor has been used in different scientific and artistic works to visualize set systems.<br><br> Hypergraphs are used to model set systems. Each element in the set system is represented by a vertex and each hyperedge is representing a set.'
          this.setup()
          break
        case 1:
          this.header = 'Preliminary'
          this.text = 'The color of vertices should indicate to what hyperedge a vertex is incident to. Grey vertices are incident to multiple hyperedges.'
          this.step1()
          break
        case 2:
          this.header = 'Simplification'
          this.text = 'The MetroSets system is realized as a modular 4-step pipeline. We propose and implement at least two different algorithms for each step. Many underlying problems are considered NP hard, therefore we simplify the input hypergraph.<br><br>The pre-processing step of our pipeline creates a combinatorally equivalent instance of our problem by setting aside all single hyperedge vertices and merging vertices that are incident to the exact same set of hyperedges into a dummy vertex.<br><br>For now we place those vertices on the left.'
          this.step2()
          break
        case 3:
          this.header = 'Support Graph'
          this.text = 'We need to define a total order over the vertices of each hyperedge, as we do not have contextual information of stations along a metro line yet. This is equivalent with finding a path-based support graph of the hypergraph, where each subgraph induced by the hyperedges contains a Hamiltonian path. <br><br>We provide two different algorithms for this task. The first method tries to minimize the total number of edges needed to draw the support graph, such that whenever the intersection of two hyperedges contains two or more vertices, the hyperedges should be drawn in parallel, rather than branching of and rejoining. The second approach assigns a cost to each edge in the support graph, which is lower for pairs of vertices that share many hyperedges, and attempts to find an ordering which minimizes this cost for each path.<br><br>After this step we have a support graph of the simplified hypergraph and can now handle the remaining vertices.'
          this.step22()
          break
        case 4:
          this.header = 'Expansion and Insertion'
          this.text = 'The  merged vertices can be expanded trivially. Vertices belonging to single hyperedges present a more interesting choice, as they could in principle be introduced at any position in the given path.<br><br>'
          this.step222()
          break
        case 5:
          this.header = 'Inserting the Set-Aside Vertices'
          this.text = 'The first-viable approach introduces vertices at the beginning or end of their respective support path, leading to long, trailing lines of single-hyperedge vertices. The split-insert approach instead places half of the vertices at the beginning of the line, and distributes the others evenly throughout all of the candidate positions along the path.<br><br>The example here uses the first-viable approach to insert the set-aside vertices.'
          this.step3()
          break
        case 6:
          this.header = 'Initial Layout'
          this.text = 'After the support graph is constructed, we determine an initial layout for it. The first method is a force-based approach, which embeds the graph purely based on the structure determined in the previous steps. The second approach instead iterates between first laying out the graph, using either Kamada-Kawai or Neato, and then secondly revising the ordering of vertices each path, incorporating the Euclidean distance between vertices into the cost function.<br><br>Up until this step we do not have an embedding for the graph, even though the example may imply it.'
          break
        case 7:
          this.header = 'Schematization'
          this.text = 'Schematizing a metro map is the last step in the pipeline before we label the map. The previously determined embedding is used as input for the schematization algorithm. We implemented the force-based schematization approach of <a href="https://link.springer.com/chapter/10.1007%2F978-3-662-44043-8_1" target="blank">Chivers and Rodgers</a>, as well as an adapted version of a <a href="https://dl.acm.org/doi/abs/10.1145/3274895.3274959" target="blank">least squares approximation.</a>'
          this.step4()
          break
        case 8:
          this.header = 'Post-Processing'
          this.text = 'As a last step we perform a labeling algorithm and optimize line crossings.'
          this.step7()
          break
        case 9:
          this.header = 'Our tour has ended'
          this.text = 'Thank you for watching our tour! If you would like to explore MetroSets further, you can use one of our provided example datasets, or else upload your own data.'
          this.showButton = true
          break
      }
      this.state = this.state + 1
    },

    parseData (error, data) {
      if (error) {
        return
      }

      var rawData = JSON.parse(data)
      this.data = rawData
    },

    setup () {
      var options = jsonStep4.output.options
      var map = jsonStep4.output

      map.nodes.forEach((value) => {
        this.nodeLineSet[value.Id] = new Set()
        this.nodeDummy[value.Id] = false
        this.numNodes++
      })

      map.paths.forEach((value) => {
        var set = new Set()

        value.Points.forEach(p => {
          if (p.Id >= 0) {
            set.add(p.Id)
            this.nodeLineSet[p.Id].add(value.Label)
          }
        })
        this.lineSet[value.Label] = set
        this.colors[value.Label] = value.Color
      })

      function seteq (a, b) {
        if (a !== b) {
          if (!a || !b) { return false }
          for (var e of a) {
            if (!b.has(e)) { return false }
          }
        }
        return true
      }

      for (var i = 0; i < map.nodes.length; i++) {
        for (var j = i + 1; j < map.nodes.length; j++) {
          var node1 = map.nodes[i]
          var node2 = map.nodes[j]

          if (seteq(this.nodeLineSet[node1.Id], this.nodeLineSet[node2.Id])) {
            this.nodeDummy[node2.Id] = true
          }
        }
      }

      var dW = this.$refs.drawAreaRef.clientWidth
      var dH = this.$refs.drawAreaRef.clientHeight
      var scaleX = 1
      var scaleY

      if (dW / options.width > dH / options.height) {
        scaleX = dH / options.height
        scaleY = dH / options.height
      } else {
        scaleX = dW / options.width
        scaleY = dW / options.width
      }

      var xScale = d3Scale.scaleLinear().domain([0, options.width]).range([10, (options.width * scaleX - 10)])
      var yScale = d3Scale.scaleLinear().domain([0, options.height]).range([10, (options.height * scaleY - 10)])

      this.xScale = xScale
      this.yScale = yScale
      this.dW = dW
      this.dH = dH

      var svg = d3.select('#drawArea').append('g')

      this.chartLayer2 = svg
        .append('g')
      this.chartLayer1 = svg
        .append('g')

      // create circle nodes
      var nodeGroup = this.chartLayer1
        .selectAll('circle')
        .data(map.nodes.filter(function (d) { return d['IsDummy'] === false }))
        .enter()

      // create tick nodes
      nodeGroup
        .append('circle')
        .attr('class', 'node')
        .attr('cx', g => dW / 2)
        .attr('cy', g => dH / 2)
        // .attr('r', g => this.lineStroke / 2 * this.scaleStationLinear(g['Id']))
        .attr('r', g => 5)
        .style('fill', 'white')
        .style('stroke', 'black')
        .style('stroke-width', 2)

      this.line = d3Shape.line().curve(d3.curveCardinal.tension(0.90)).x(d => {
        return xScale(d['X'] + this.lineStroke * d['VX'])
      }).y(d => yScale(d['Y'] + this.lineStroke * d['VY']))
    },

    step1 () {
      var tthis = this
      var map = jsonStep4.output

      d3.selectAll('.node')
        .transition()
        .duration(750)
        .ease(d3.easePolyInOut)
        .attr('cx', g => Math.random() * this.dW)
        .attr('cy', g => Math.random() * this.dH)
        .style('fill', g => {
          if (tthis.nodeLineSet[map.nodes[g['Id']]['Id']].size !== 1 || !tthis.nodeDummy[map.nodes[g['Id']]['Id']]) {
            return '#777777'
          } else {
            return tthis.colors[tthis.nodeLineSet[g['Id']].values().next().value]
          }
        })
    },

    step2 () {
      var tthis = this
      var map = jsonStep4.output

      d3.selectAll('.node')
        .filter(d => tthis.nodeLineSet[map.nodes[d['Id']]['Id']].size === 1 || tthis.nodeDummy[map.nodes[d['Id']]['Id']])
        .transition()
        .duration(750)
        .ease(d3.easePolyInOut)
        .attr('cy', function (g, i) {
          return tthis.dH / tthis.numNodes * i
        })
        .attr('cx', 20)
    },

    step22 () {
      var tthis = this
      var map = jsonStep3.output

      var segments = this.getFlatLineNon1(map, false)

      var lineGroup = this.chartLayer2
        .selectAll('.line')
        .data(segments)
        .enter()

      lineGroup
        .append('line')
        .attr('class', 'line')
        .attr('id', d => d['Id'])
        .style('stroke', d => d['Color'])
        .style('stroke-width', this.lineStroke)
        .attr('x1', d => tthis.xScale(d['X1']))
        .attr('y1', d => tthis.yScale(d['Y1']))
        .attr('x2', d => tthis.xScale(d['X2']))
        .attr('y2', d => tthis.yScale(d['Y2']))

      d3.selectAll('.node')
        .filter(d => tthis.nodeLineSet[map.nodes[d['Id']]['Id']].size > 1 && !tthis.nodeDummy[map.nodes[d['Id']]['Id']])
        .transition()
        .duration(750)
        .ease(d3.easePolyInOut)
        .attr('cy', d => tthis.yScale(map.nodes[d['Id']]['Y']))
        .attr('cx', d => tthis.xScale(map.nodes[d['Id']]['X']))
        .style('fill', '#FFFFFF')
    },

    step222 () {
      var tthis = this
      var map = jsonStep3.output

      var segments = this.getFlatLineNon1(map, true)

      this.chartLayer2
        .selectAll('.line')
        .remove()

      var lineGroup = this.chartLayer2
        .selectAll('.line')
        .data(segments)

      lineGroup
        .exit()
        .remove()

      lineGroup
        .enter()
        .append('line')
        .attr('class', 'line')
        .attr('id', d => d['Id'])
        .style('stroke', d => d['Color'])
        .style('stroke-width', this.lineStroke)
        .attr('x1', d => tthis.xScale(d['X1']))
        .attr('y1', d => tthis.yScale(d['Y1']))
        .attr('x2', d => tthis.xScale(d['X2']))
        .attr('y2', d => tthis.yScale(d['Y2']))

      d3.selectAll('.node')
        .filter(d => tthis.nodeLineSet[map.nodes[d['Id']]['Id']].size > 1)
        .transition()
        .duration(750)
        .ease(d3.easePolyInOut)
        .attr('cy', d => tthis.yScale(map.nodes[d['Id']]['Y']))
        .attr('cx', d => tthis.xScale(map.nodes[d['Id']]['X']))
        .style('fill', '#FFFFFF')
    },

    step3 () {
      var tthis = this
      var map = jsonStep3.output

      var segments = this.getFlatLine(map)

      var lineGroup = this.chartLayer2
        .selectAll('.line')

      lineGroup
        .remove()

      lineGroup = this.chartLayer2
        .selectAll('.line')
        .data(segments)
        .enter()

      lineGroup
        .append('line')
        .attr('class', 'line')
        .attr('id', d => d['Id'])
        .style('stroke', d => d['Color'])
        .style('stroke-width', this.lineStroke)
        .attr('x1', (d, i) => tthis.xScale(segments[i]['X1']))
        .attr('y1', (d, i) => tthis.yScale(segments[i]['Y1']))
        .attr('x2', (d, i) => tthis.xScale(segments[i]['X2']))
        .attr('y2', (d, i) => tthis.yScale(segments[i]['Y2']))

      d3.selectAll('.node')
        .transition()
        .duration(750)
        .ease(d3.easePolyInOut)
        .attr('cy', g => this.yScale(map.nodes[g['Id']]['Y']))
        .attr('cx', g => this.xScale(map.nodes[g['Id']]['X']))
        .style('fill', '#FFFFFF')
    },

    step4 () {
      var tthis = this
      var map = jsonStep4.output

      var segments = this.getFlatLine(map)

      this.chartLayer2
        .selectAll('.line')
        .transition()
        .duration(750)
        .ease(d3.easePolyInOut)
        .style('stroke', d => d['Color'])
        .attr('x1', (d, i) => tthis.xScale(segments[i]['X1']))
        .attr('y1', (d, i) => tthis.yScale(segments[i]['Y1']))
        .attr('x2', (d, i) => tthis.xScale(segments[i]['X2']))
        .attr('y2', (d, i) => tthis.yScale(segments[i]['Y2']))

      d3.selectAll('.node')
        .transition()
        .duration(750)
        .ease(d3.easePolyInOut)
        .attr('cy', g => this.yScale(map.nodes[g['Id']]['Y']))
        .attr('cx', g => this.xScale(map.nodes[g['Id']]['X']))
    },

    step7 () {
      var map = jsonStep4.output

      var textGroup = this.chartLayer1
        .selectAll('text')
        .data(map.labels)
        .enter()

      textGroup
        .append('text')
        .attr('class', 'text')
        .attr('x', g => (this.xScale(g['X'])))
        .attr('y', g => this.yScale(g['Y']))
        .html(g => {
          if (g['Abbr']) {
            return g['Text'].substring(0, 14) + '..'
          } else {
            return g['Text']
          }
        })
        .style('shape-rendering', 'crisp-edges')
        .style('stroke', 'none')
        .style('font-size', 10 + 'px')
        .style('font-family', 'PT Sans Narrow')
        // .style('font-family', 'Roboto Mono, monospace')
        .style('text-anchor', g => {
          switch (g['Align']) {
            case 'C':
              return 'middle'
            case 'R':
              return 'end'
            case 'L':
              return 'start'
            default:
              return 'middle'
          }
        })
        .attr('transform', g => 'rotate(' + g['Angle'] + ' ' + this.xScale(g['X']) + ' ' + this.yScale(g['Y']) + ')')
        .style('pointer-events', g => {
          if (g['Text'].length > 16) {
            return 'auto'
          } else {
            return 'none'
          }
        })
    },

    getFlatLineNon1 (map, useDummy) {
      var segments = []
      var c = 0
      map.paths.forEach((value) => {
        var current = null
        var next = null

        if (this.nodeLineSet[value.Points[0]['Id']].size > 1 && (useDummy || !this.nodeDummy[value.Points[0]['Id']])) {
          current = value.Points[0]
        }

        for (var i = 1; i < value.Points.length; i++) {
          next = value.Points[i]

          if (this.nodeLineSet[value.Points[i]['Id']].size > 1 && (useDummy || !this.nodeDummy[value.Points[i]['Id']])) {
            if (current === null) {
              current = next
            } else {
              var p1 = current
              var p2 = next

              var seg = {}
              seg['X1'] = p1['X'] + this.lineStroke * p1['VX']
              seg['X2'] = p2['X'] + this.lineStroke * p2['VX']
              seg['Y1'] = p1['Y'] + this.lineStroke * p1['VY']
              seg['Y2'] = p2['Y'] + this.lineStroke * p2['VY']
              seg['Id'] = c++
              seg['Color'] = value['Color']
              segments.push(seg)

              current = next
            }
          }
        }
      })

      return segments
    },

    getFlatLine (map) {
      var segments = []
      var c = 0
      map.paths.forEach((value) => {
        for (var i = 0; i < value.Points.length - 1; i++) {
          var p1 = value.Points[i]
          var p2 = value.Points[i + 1]

          var seg = {}
          seg['X1'] = p1['X'] + this.lineStroke * p1['VX']
          seg['X2'] = p2['X'] + this.lineStroke * p2['VX']
          seg['Y1'] = p1['Y'] + this.lineStroke * p1['VY']
          seg['Y2'] = p2['Y'] + this.lineStroke * p2['VY']
          seg['Id'] = c++
          seg['Color'] = value['Color']
          segments.push(seg)
        }
      })

      return segments
    }
  }
}
</script>

<style>
.v-dialog--fullscreen {
     border-radius: 0;
     min-width: 100vw;
     background-color: white;
     margin: 0;
     max-height: 100%;
     position: fixed;
     overflow-y: auto;
     top: 0;
     left: 0;
}
</style>
